/**
 * Reference Link Item
 * Renders an icon + styled link for document/external references
 */

import { Link, makeStyles, tokens } from '@fluentui/react-components'
import { DocumentRegular, LinkRegular } from '@fluentui/react-icons'
import type { InsightContentItem } from '../../../../domain'
import { validateSafeUrl, isExternalUrl } from '../../../../utils/urlUtils'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  icon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  link: {
    color: tokens.colorBrandForeground1,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
})

export interface ReferenceLinkItemProps {
  item: InsightContentItem
}

export function ReferenceLinkItem({ item }: ReferenceLinkItemProps) {
  const styles = useStyles()

  // Determine icon based on href type
  const isExternal = isExternalUrl(item.href)
  const Icon = isExternal ? LinkRegular : DocumentRegular

  // Validate URL for safety
  const { href } = validateSafeUrl(item.href)

  return (
    <div className={styles.root}>
      <Icon className={styles.icon} />
      <Link
        href={href}
        className={styles.link}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
      >
        {item.content}
      </Link>
    </div>
  )
}
